import { Spin } from "antd";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type { ContractVirtualAppProfileListEntity } from "src/app/features/Contract/types/subTypes/ContractVirtualAppProfileListEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { useLazyLoadPhysicalAppProfilesVirtualQuery } from "../ContractsAcceptedCardTypesTable/ContractsAcceptedCardTypesServerApi";
import type { LoadContractsAppProfilesRequest } from "../ContractsAcceptedCardTypesTable/types";

type ComponentState = {
    siteId?: number;
    centralized?: boolean;
    shouldUpdateData: boolean;
    queryConfig?: LoadContractsAppProfilesRequest;
};

export interface ContractsVirtualAppProfilesTableProps
    extends AntCustomFormField<ContractVirtualAppProfileListEntity[]>,
        CommonFieldAttributes {
    id: number;
    siteId?: number;
    centralized?: boolean;
}

export const ContractsVirtualAppProfilesTable: FC<ContractsVirtualAppProfilesTableProps> = ({
    value = [],
    onChange,
    readOnly,
    id,
    siteId,
    centralized,
}) => {
    const getColumnsConfig = (t: Function): ExtendedColumnsType<ContractVirtualAppProfileListEntity>[] => {
        return [
            {
                title: t("genericTable.column.id"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("contractCreateForm.name"),
                key: "name",
                dataIndex: "name",
            },
        ];
    };

    const dataGroupCode = useMemo(() => getAssignedDataGroup().code, []);

    const onDataChanged = useCallback(
        (tableValue: ContractVirtualAppProfileListEntity[]) => {
            if (onChange) {
                onChange(tableValue);
            }
        },
        [onChange],
    );

    const [state, setState] = useState<ComponentState>({
        shouldUpdateData: false,
        centralized,
        siteId,
    });

    const [execQuery, { isFetching }] = useLazyLoadPhysicalAppProfilesVirtualQuery();

    // Build query config based on prop changes and state
    const buildQueryConfig = useCallback(() => {
        if (state.siteId === undefined) {
            return {
                siteId,
                centralized,
                shouldUpdateData: false,
            };
        }

        const centralizedHasChanged = state.centralized !== centralized && state.centralized !== undefined;
        const siteIdHasChanged = state.siteId !== siteId && state.siteId !== undefined;
        const shouldUpdateData = !!siteId && (centralizedHasChanged || siteIdHasChanged);

        if (shouldUpdateData) {
            const siteParams = `${id}/`;
            const siteIdString = `?siteId=${siteId}`;
            const centralizedString = `&centralized=${centralized ?? false}`;

            const queryParams = `${siteIdString}${centralizedString}`;

            const queryConfig = { siteParams, dataGroupCode, queryParams };

            return {
                siteId,
                centralized,
                queryConfig,
                shouldUpdateData,
            };
        }
        return null;
    }, [state, centralized, siteId, id, dataGroupCode]);

    // Watch for prop changes and update state
    useEffect(() => {
        const result = buildQueryConfig();
        if (result) {
            setState(result);
        }
    }, [buildQueryConfig]);

    useEffect(() => {
        if (state.shouldUpdateData && state.queryConfig) {
            execQuery(state.queryConfig)
                .unwrap()
                .then((data) => {
                    onDataChanged(data.items);
                })
                .catch(() => {});
            setState({ ...state, shouldUpdateData: false });
        }
    }, [state, setState, execQuery, onDataChanged]);

    return (
        <Spin spinning={isFetching}>
            <PropertyTable<ContractVirtualAppProfileListEntity>
                name="contractsVirtualAppProfiles_table"
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="id"
                tableData={value}
                readOnly={readOnly || false}
                rowsAreSelectable={false}
            />
        </Spin>
    );
};
